import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { API, adminHeaders } from '../../lib/shop';
import { useShopConfig } from '../../contexts/ShopConfigContext';

export const AdminSettings = () => {
  const { live, refresh } = useShopConfig();
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shop/admin/subscribers`, { headers: adminHeaders() }).then(r => setSubs(r.data)).catch(() => toast.error('Failed to load subscribers'));
    axios.get(`${API}/quotes/email-status`).then(r => setEmailStatus(r.data)).catch(() => {});
  }, []);

  const toggle = async (value) => {
    if (value && !window.confirm('Open the shop to the public? Customers will be able to buy products with real card payments.')) return;
    setSaving(true);
    try {
      await axios.put(`${API}/shop/admin/config`, { live: value }, { headers: adminHeaders() });
      await refresh();
      toast.success(value ? 'Shop is now LIVE' : 'Shop set to Coming Soon');
    } catch { toast.error('Could not update setting'); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const csv = ['email,name,signed_up', ...subs.map(s => `${s.email},${(s.name || '').replace(/,/g, ' ')},${s.created_at}`)].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'shop-subscribers.csv';
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-gray-900">Shop status: <span className={live ? 'text-green-600' : 'text-amber-600'} data-testid="shop-status-label">{live ? 'LIVE' : 'COMING SOON'}</span></p>
          <p className="text-sm text-gray-600">When off, visitors see the Coming Soon page and checkout is disabled.</p>
        </div>
        <Switch checked={live} onCheckedChange={toggle} disabled={saving} data-testid="shop-live-toggle" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <p className="font-semibold text-gray-900 mb-1">Email delivery</p>
        {emailStatus ? (
          emailStatus.configured
            ? <p className="text-sm text-green-700" data-testid="email-status">Configured via {emailStatus.provider} (sender: {emailStatus.sender}) → notifications go to {emailStatus.business_email}</p>
            : <p className="text-sm text-amber-700" data-testid="email-status">Not configured. Add <code>RESEND_API_KEY</code> (and <code>SENDER_EMAIL</code> on a verified domain) to enable quote, order and signup emails. Requests are still saved here.</p>
        ) : <p className="text-sm text-gray-500">Checking...</p>}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold text-gray-900">Launch notification signups <span className="text-gray-400 font-normal">({subs.length})</span></p>
          {subs.length > 0 && <button onClick={exportCsv} className="text-sm text-pink-600 hover:underline" data-testid="export-subscribers">Export CSV</button>}
        </div>
        {subs.length === 0 ? <p className="text-sm text-gray-500" data-testid="subscribers-empty">No signups yet.</p> : (
          <ul className="divide-y text-sm" data-testid="subscribers-list">
            {subs.map(s => (
              <li key={s.id} className="py-2 flex justify-between gap-3">
                <span className="text-gray-900">{s.email}</span>
                <span className="text-gray-500">{new Date(s.created_at).toLocaleDateString('en-GB')}{s.confirmation?.sent ? ' · confirmed' : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
